import {useMemo, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {openBankingActions} from '../../state/actions/openBanking.actions';
import {useOpenBankingAccounts} from './useOpenBankingAccounts';
import {useOpenBankingAccountAccessIds} from './useOpenBankingAccountAccessIds';

// Loads a cursor-paginated OB list to completion. `getFilterParams` optionally
// returns extra action params (e.g. account_ids) merged into every page request.
export const useLoadOpenBankingPaged = (actionCreator, getFilterParams) => {
  const dispatch = useDispatch();
  const loadRef = useRef(null);

  loadRef.current = (cursor, companyId) => {
    dispatch(actionCreator({
      cursor,
      companyId,
      ...(getFilterParams?.() ?? {}),
      // The action anchors this walk to the company that was active when it started
      // (companyId, echoed back here) and silently drops any page that resolves after
      // the user has since switched companies — so a late page can't repopulate the
      // new company's state with the old company's accounts (H1MVP-5024).
      successCallback: (data, startCompanyId) => {
        const nextCursor = data?.pagination?.next_cursor;
        if (nextCursor) loadRef.current(nextCursor, startCompanyId);
      },
      // Surface failures — otherwise a failed load leaves the list empty and the
      // consumers (e.g. OB bank-name resolution) silently keep showing stale data.
      errorCallback: (err) => {
        console.error('Failed to load open banking paged list', err);
      },
    }));
  };

  return () => loadRef.current();
};

// Managers/Users are scoped to the accounts they were granted access to;
// admins have no scoped ids, so the accounts load unfiltered.
export const useLoadOpenBankingAccounts = () => {
  const accountIds = useOpenBankingAccountAccessIds();
  return useLoadOpenBankingPaged(
    openBankingActions.getAccounts,
    () => (accountIds.length ? {accountIds} : {})
  );
};

// Connections are scoped to those owning the already-loaded (scoped) accounts.
// With no scoped account ids (admins) the connections load unfiltered.
export const useLoadOpenBankingConnections = () => {
  const accounts = useOpenBankingAccounts();
  const accountAccessIds = useOpenBankingAccountAccessIds();

  const connectionIds = useMemo(
    () => [...new Set(accounts.map((account) => account.connection_id).filter(Boolean))],
    [accounts]
  );

  return useLoadOpenBankingPaged(
    openBankingActions.getConnections,
    () => (accountAccessIds.length && connectionIds.length ? {connectionIds} : {})
  );
};
